/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable jsx-a11y/no-static-element-interactions */

import {
  Ban,
  ChevronDown,
  ChevronUp,
  Cog,
  Loader2,
  Music,
  Pencil,
  RefreshCw,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Button from 'shared_components/components/Button';
import { useApi } from 'shared_components/context';
import { useClientId } from 'shared_components/context/client';
import {
  useDiscographyIds,
  DiscographyId,
  DiscographySourceType,
  SourceId,
} from './useDiscographyIds';
import {
  collectionNotice,
  isActiveCollectionStatus,
} from '../artistIdCollectionModel';
import { ArtistDiscographySourceIdCell } from './ArtistDiscography/ArtistDiscographySourceIdCell';
import {
  artistSourceIdsAreNotValid,
  generateArtistUrlBySource,
} from './ArtistDiscography/utils';
import AliasModal, { AliasForm, AliasId } from './ArtistDiscography/AliasModal';
import { set } from 'lodash';
import AliasBandManagement from './ArtistDiscography/AliasBandManagement';
import { BandForm, BandId } from './ArtistDiscography/BandModal';
import WModal from 'shared_components/components/WModal';
import WButton from 'shared_components/components/WForms/WButton/WButton';
import PermanentStatusCheck from './ArtistDiscography/PermanentStatusCheck';
import DiscographyIdFilterDropdown, {
  DiscographyIdFilter,
} from './ArtistDiscography/DiscographyIdFilterDropdown';
import { PipelineRunStatusStatusEnum } from 'shared_components/generated/admin';
import { usePrimaryDiscographyRun } from './usePrimaryDiscographyRun';

const DEFAULT_ALIAS_FORM_STATE: AliasForm = {
  aliasName: '',
  isGroup: false,
  isPermanentMember: false,
  ids: [
    {
      source: 'discogs',
      itemId: 'discogs-0',
      id: '',
      valid: false,
      approved: true,
      rejected: false,
      created_by_system: false,
    },
    {
      source: 'deezer',
      itemId: 'deezer-0',
      id: '',
      valid: false,
      approved: true,
      rejected: false,
      created_by_system: false,
    },
    {
      source: 'spotify',
      itemId: 'spotify-0',
      id: '',
      valid: false,
      approved: true,
      rejected: false,
      created_by_system: false,
    },
    {
      source: 'musicbrainz',
      itemId: 'musicbrainz-0',
      id: '',
      valid: false,
      approved: true,
      rejected: false,
      created_by_system: false,
    },
  ],
  manualStageName: false,
  hasAutoPopulated: false,
  errors: {},
};

const DEFAULT_BAND_FORM_STATE: BandForm = {
  stageName: '',
  isPermanentMember: false,
  isMainPerformer: false,
  linkedAliasId: null,
  bandMode: 'new',
  ids: [
    {
      source: 'discogs',
      itemId: 'discogs-0',
      id: '',
      valid: false,
      approved: true,
      rejected: false,
      created_by_system: false,
    },
    {
      source: 'deezer',
      itemId: 'deezer-0',
      id: '',
      valid: false,
      approved: true,
      rejected: false,
      created_by_system: false,
    },
    {
      source: 'spotify',
      itemId: 'spotify-0',
      id: '',

      valid: false,
      approved: true,
      rejected: false,
      created_by_system: false,
    },
    {
      source: 'musicbrainz',
      itemId: 'musicbrainz-0',
      id: '',
      valid: false,
      approved: true,
      rejected: false,
      created_by_system: false,
    },
  ],
  manualStageName: false,
  hasAutoPopulated: false,
  errors: {},
};

/* eslint-disable jsx-a11y/click-events-have-key-events */
export function ArtistDiscographyIDContent({
  onUserSettingsUpdated,
  toggleSearchPanel,
  isSearchExpanded,
}: {
  readonly onUserSettingsUpdated: () => void;
  readonly toggleSearchPanel: () => void;
  readonly isSearchExpanded: boolean;
}) {
  const { clientId } = useClientId();
  const { adminApi: api } = useApi();
  const {
    collectedDiscographyIds: _collectedDiscographyIds,
    status,
    indicator,
    errorDetail,
    elapsed,
    isStalled,
    runEndedWhileWatching,
    collectedIdsKnown,
    canCancel,
    getCollectedDiscographyIds,
    cancelAutoCollectIds,
    cancelAutoCollectIdsInProgress,
  } = useDiscographyIds();

  const [tableFilter, setTableFilter] = useState<DiscographyIdFilter>('all');

  const collectedDiscographyIds = useMemo(() => {
    if (tableFilter === 'verified') {
      const verifiedFilter = (id: SourceId) =>
        !id.rejected && (id.approved || id.valid);
      return _collectedDiscographyIds
        .filter((id) =>
          Object.entries(id.source_ids).some(([, source_id_list]) => {
            return source_id_list.some(verifiedFilter);
          })
        )
        .map((artist) => ({
          ...artist,
          source_ids: {
            discogs: [...artist.source_ids.discogs.filter(verifiedFilter)],
            deezer: [...artist.source_ids.deezer.filter(verifiedFilter)],
            musicbrainz: [
              ...artist.source_ids.musicbrainz.filter(verifiedFilter),
            ],
            spotify: [...artist.source_ids.spotify.filter(verifiedFilter)],
          },
        }));
    } else if (tableFilter === 'rejected') {
      return _collectedDiscographyIds
        .filter((id) =>
          Object.entries(id.source_ids).some(([, source_id_list]) => {
            return source_id_list.some(
              (source_id) => source_id.rejected === true
            );
          })
        )
        .map((artist) => ({
          ...artist,
          source_ids: {
            discogs: [...artist.source_ids.discogs.filter((id) => id.rejected)],
            deezer: [...artist.source_ids.deezer.filter((id) => id.rejected)],
            musicbrainz: [
              ...artist.source_ids.musicbrainz.filter((id) => id.rejected),
            ],
            spotify: [...artist.source_ids.spotify.filter((id) => id.rejected)],
          },
        }));
    } else if (tableFilter === 'uncertain') {
      return _collectedDiscographyIds
        .filter((id) =>
          Object.entries(id.source_ids).some(([, source_id_list]) => {
            return source_id_list.some(
              (source_id) =>
                !source_id.valid && !source_id.approved && !source_id.rejected
            );
          })
        )
        .map((artist) => ({
          ...artist,
          source_ids: {
            discogs: [
              ...artist.source_ids.discogs.filter(
                (id) => !id.valid && !id.approved && !id.rejected
              ),
            ],
            deezer: [
              ...artist.source_ids.deezer.filter(
                (id) => !id.valid && !id.approved && !id.rejected
              ),
            ],
            musicbrainz: [
              ...artist.source_ids.musicbrainz.filter(
                (id) => !id.valid && !id.approved && !id.rejected
              ),
            ],
            spotify: [
              ...artist.source_ids.spotify.filter(
                (id) => !id.valid && !id.approved && !id.rejected
              ),
            ],
          },
        }));
    }

    return _collectedDiscographyIds;
  }, [_collectedDiscographyIds, tableFilter]);

  const [sortedArtistData, setSortedArtistData] = useState(
    collectedDiscographyIds
  );

  const [sortDirection, setSortDirection] = useState('asc');

  // Function to sort artist data
  const sortArtistData = useCallback(
    (discogIds: DiscographyId[]) => {
      const sorted = [...discogIds].sort((a, b) => {
        if (sortDirection === 'asc') {
          return a.stageName.localeCompare(b.stageName);
        } else {
          return b.stageName.localeCompare(a.stageName);
        }
      });
      setSortedArtistData(sorted);
    },
    [sortDirection]
  );

  // Function to toggle sort direction
  const toggleSortDirection = useCallback(() => {
    setSortDirection((state) => (state === 'asc' ? 'desc' : 'asc'));
  }, []);

  useEffect(() => {
    if (Array.isArray(collectedDiscographyIds)) {
      console.log('Sorting discog id list');
      sortArtistData(collectedDiscographyIds);
    }
  }, [collectedDiscographyIds, sortDirection]);

  // Only a genuinely active run takes the card over. `unknown` must NOT: it is the state on
  // every mount before the first poll answers, and blanking the whole discography on each
  // page load to show a spinner is worse than the bug being fixed. It gets the banner below
  // instead, which reports the lost contact without hiding anything.
  // A terminal banner is re-shown on every mount — the server keeps reporting the last run
  // as FAILED forever — so it needs a way out. Dismissal is keyed on the run being reported,
  // so a *new* failure re-arms it rather than inheriting the old dismissal.
  const [dismissedNoticeKey, setDismissedNoticeKey] = useState<string | null>(
    null
  );
  const noticeKey = `${clientId ?? ''}:${status}:${errorDetail ?? ''}`;
  const noticeDismissed = dismissedNoticeKey === noticeKey;

  // ...and not once a run has overrun its budget. `CANCELLING` in particular is driven by
  // its own Celery task, so a dead worker or an unreachable Dagster used to leave the whole
  // discography behind a spinner with no cancel button, no banner and no way out. Showing
  // the data plus an amber banner is strictly better than hiding it behind a stuck spinner.
  const isPopulatingIds = isActiveCollectionStatus(status) && !isStalled;
  // Terminal-but-noteworthy outcomes. These have `busy: false`, so without their own
  // rendering path a failed collection would finish completely silently — the admin would
  // see the button re-enable and no explanation, which is how the repeat-clicking starts.
  // `collectionNotice` decides which outcomes are worth a banner at all.
  const runNotice = noticeDismissed
    ? null
    : collectionNotice(status, {
        idsKnown: collectedIdsKnown,
        hasIds: _collectedDiscographyIds.length > 0,
        endedWhileWatching: runEndedWhileWatching,
        elapsed,
        isStalled,
        errorDetail,
      });
  const idsPopulated = sortedArtistData.length > 0;

  const tableBodyRef = useRef(null);

  const [credentialsFormData, setCredentialsFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    pplAccountName: '',
    pplPerformerName: '',
    pplPerformerId: '',
  });

  const [cleanCredentialsFormData, setCleanCredentialsFormData] =
    useState(credentialsFormData);

  useEffect(() => {
    if (!clientId) return;
    api
      .retrieveUserSettings({ userId: clientId })
      .then((userSettings) => {
        api
          .updateUserSettings({
            userId: clientId,
            userSettings: {
              ...userSettings,
              firstName: credentialsFormData.firstName,
              lastName: credentialsFormData.lastName,
              emailAddress: credentialsFormData.email,
              pplAccountName: credentialsFormData.pplAccountName,
              pplPerformerName: credentialsFormData.pplPerformerName,
            },
          })
          .catch((e) => {
            console.error(e);
          });
      })
      .catch((e) => {
        console.error(e);
      });
  }, [credentialsFormData]);

  const fetchData = useCallback((clientId: string) => {
    api
      .retrieveUserSettings({ userId: clientId })
      .then((userSettings) => {
        setCredentialsFormData({
          firstName: userSettings.firstName ?? '',
          lastName: userSettings.lastName ?? '',
          email: userSettings.emailAddress ?? '',
          pplAccountName: userSettings.pplAccountName ?? '',
          pplPerformerName: userSettings.pplPerformerName ?? '',
          pplPerformerId: userSettings.pplPerformerId ?? '',
        });
      })
      .catch((e) => {
        console.error(e);
      });
  }, []);

  useEffect(() => {
    if (clientId) {
      console.log('Refetching user settings for artis discography contents');
      fetchData(clientId);
    }
  }, [clientId]);

  // Add new state for artist details modal
  const [showEditArtistModal, setShowEditArtistModal] = useState(false);

  const [showIdDetailsModal, setShowIdDetailsModal] = useState(false);
  const [selectedIdDetails, setSelectedIdDetails] = useState<{
    id: string;
    artistName: string;
    platform: keyof DiscographyId['source_ids'];
    source_ids: DiscographyId['source_ids'][keyof DiscographyId['source_ids']];
  }>({
    id: '',
    artistName: '',
    platform: 'discogs',
    source_ids: [],
  });

  const [confirmDeleteModalDetails, setConfirmDeleteModalDetails] = useState<{
    stageName: string;
    type: string;
    id: string;
  } | null>(null);

  const handleDeleteArtistDiscographyID = (id: string) => {
    if (!clientId) return;

    const aliasOrBand = sortedArtistData.find((aob) => aob.id === id);

    if (aliasOrBand?.type === 'persona') {
      handDeleteArtistAlias(id);
    } else if (aliasOrBand?.type === 'group') {
      handDeleteArtistBand(id, aliasOrBand);
    }
  };

  const handDeleteArtistAlias = (aliasId: string) => {
    if (!clientId) return;

    api.removeAliasRemove({ userId: clientId, aliasId }).then(() => {
      getCollectedDiscographyIds(clientId);
    });
  };

  const handDeleteArtistBand = (
    bandId: string,
    band?: (typeof sortedArtistData)[0]
  ) => {
    if (!clientId) return;

    // Find the band if not provided
    const bandData =
      band ||
      sortedArtistData.find((aob) => aob.id === bandId && aob.type === 'group');

    if (!bandData) {
      console.error('Band not found:', bandId);
      return;
    }

    // Check if band has a linked alias
    if (bandData.linked_alias_id) {
      // Unlink the alias from the band instead of deleting the band
      console.log('Unlinking alias from band:', {
        bandId,
        aliasId: bandData.linked_alias_id,
      });

      api
        .unlinkAliasFromBandArtistBand({
          userId: clientId,
          bandId: bandId,
          unlinkAliasFromBand: {
            aliasId: bandData.linked_alias_id,
          },
        })
        .then(() => {
          console.log('✅ Successfully unlinked alias from band');
          getCollectedDiscographyIds(clientId);
        })
        .catch((error) => {
          console.error('Error unlinking alias from band:', error);
          // Optionally show an error message to the user
        });
    } else {
      console.warn('Band has no linked alias, cannot unlink:', bandId);
      // If there's no linked alias, we can't unlink.
      // Since the requirement is to not delete, we'll just log a warning.
      // You may want to show an error message to the user here.
    }
  };

  // Add new alias form state
  const [newAliasForm, setNewAliasForm] = useState<AliasForm>(
    DEFAULT_ALIAS_FORM_STATE
  );
  const [newBandForm, setNewBandForm] = useState<BandForm>(
    DEFAULT_BAND_FORM_STATE
  );

  // Add state for active tab
  const [activeAliasTab, setActiveAliasTab] =
    useState<DiscographySourceType>('discogs');
  const [activeBandTab, setActiveBandTab] =
    useState<DiscographySourceType>('discogs');
  const [showAddAliasModal, setShowAddAliasModal] = useState(false);
  const [showAddBandModal, setShowAddBandModal] = useState(false);

  const handleOpenEditModal = (id: string, source: DiscographySourceType) => {
    const aliasOrBand = sortedArtistData.find((a) => a.id === id);

    if (aliasOrBand?.type === 'persona') {
      handleOpenEditAliasModal(aliasOrBand.id, source);
    } else if (aliasOrBand?.type === 'group') {
      handleOpenEditBandModal(aliasOrBand.id, source);
    }
  };

  const handleOpenEditAliasModal = (
    artistId: string,
    source: DiscographySourceType
  ) => {
    const artist = sortedArtistData.find((a) => a.id === artistId);
    if (!artist) return;

    setActiveAliasTab(source);

    setNewAliasForm({
      id: artist.id,
      aliasName: artist.stageName,
      isGroup: artist.type === 'group',
      isPermanentMember: false,
      ids: [
        ...(artist.source_ids.discogs.length > 0
          ? artist.source_ids.discogs.map<AliasId>((id, index) => ({
              source: 'discogs',
              itemId: `${artist.id}-discogs-${index}`,
              id: id.id,
              artistName: id.original_name,
              validated: true,
              valid: id.valid,
              approved: id.approved,
              rejected: id.rejected,
              editing: false,
              created_by_system: id.created_by_system,
            }))
          : [
              {
                source: 'discogs',
                itemId: `${artist.id}-discogs-0`,
                id: '',
                valid: false,
                approved: true,
                rejected: false,
                created_by_system: false,
              } satisfies AliasId,
            ]),
        ...(artist.source_ids.deezer.length > 0
          ? artist.source_ids.deezer.map<AliasId>((id, index) => ({
              source: 'deezer',
              itemId: `${artist.id}-deezer-${index}`,
              id: id.id,
              artistName: id.original_name,
              validated: true,
              valid: id.valid,
              approved: id.approved,
              rejected: id.rejected,
              editing: false,
              created_by_system: id.created_by_system,
            }))
          : [
              {
                source: 'deezer',
                itemId: `${artist.id}-deezer-0`,
                id: '',
                valid: false,
                approved: true,
                rejected: false,
                created_by_system: false,
              } satisfies AliasId,
            ]),
        ...(artist.source_ids.spotify.length > 0
          ? artist.source_ids.spotify.map<AliasId>((id, index) => ({
              source: 'spotify',
              itemId: `${artist.id}-spotify-${index}`,
              id: id.id,
              artistName: id.original_name,
              validated: true,
              valid: id.valid,
              approved: id.approved,
              rejected: id.rejected,
              editing: false,
              created_by_system: id.created_by_system,
            }))
          : [
              {
                source: 'spotify',
                itemId: `${artist.id}-spotify-0`,
                id: '',
                valid: false,
                approved: true,
                rejected: false,
                created_by_system: false,
              } satisfies AliasId,
            ]),
        ...(artist.source_ids.musicbrainz.length > 0
          ? artist.source_ids.musicbrainz.map<AliasId>((id, index) => ({
              source: 'musicbrainz',
              itemId: `${artist.id}-musicbrainz-${index}`,
              id: id.id,
              artistName: id.original_name,
              validated: true,
              valid: id.valid,
              approved: id.approved,
              rejected: id.rejected,
              editing: false,
              created_by_system: id.created_by_system,
            }))
          : [
              {
                source: 'musicbrainz',
                itemId: `${artist.id}-musicbrainz-0`,
                id: '',
                valid: false,
                approved: true,
                rejected: false,
                created_by_system: false,
              } satisfies AliasId,
            ]),
      ],
      manualStageName: artist.manualStageName,
      hasAutoPopulated: artist.manualStageName ? true : false,
      errors: {},
    });
    setShowAddAliasModal(true);
  };

  const handleOpenEditBandModal = (
    artistId: string,
    source: DiscographySourceType
  ) => {
    const artist = sortedArtistData.find(
      (a) => a.id === artistId && a.type === 'group'
    );
    if (!artist) return;

    setActiveBandTab(source);

    setNewBandForm({
      id: artist.id,
      stageName: artist.stageName,
      isPermanentMember: artist.is_permanent_member ?? false,
      isMainPerformer: artist.is_main_performer ?? false,
      linkedAliasId: artist.linked_alias_id ?? null,
      ids: [
        ...(artist.source_ids.discogs.length > 0
          ? artist.source_ids.discogs.map<BandId>((id, index) => ({
              source: 'discogs',
              itemId: `${artist.id}-discogs-${index}`,
              id: id.id,
              artistName: id.original_name,
              validated: true,
              valid: id.valid,
              approved: id.approved,
              rejected: id.rejected,
              editing: false,
              created_by_system: id.created_by_system,
            }))
          : [
              {
                source: 'discogs',
                itemId: `${artist.id}-discogs-0`,
                id: '',
                valid: false,
                approved: true,
                rejected: false,
                created_by_system: false,
              } satisfies BandId,
            ]),
        ...(artist.source_ids.deezer.length > 0
          ? artist.source_ids.deezer.map<BandId>((id, index) => ({
              source: 'deezer',
              itemId: `${artist.id}-deezer-${index}`,
              id: id.id,
              artistName: id.original_name,
              validated: true,
              valid: id.valid,
              approved: id.approved,
              rejected: id.rejected,
              editing: false,
              created_by_system: id.created_by_system,
            }))
          : [
              {
                source: 'deezer',
                itemId: `${artist.id}-deezer-0`,
                id: '',
                valid: false,
                approved: true,
                rejected: false,
                created_by_system: false,
              } satisfies BandId,
            ]),
        ...(artist.source_ids.spotify.length > 0
          ? artist.source_ids.spotify.map<BandId>((id, index) => ({
              source: 'spotify',
              itemId: `${artist.id}-spotify-${index}`,
              id: id.id,
              artistName: id.original_name,
              validated: true,
              valid: id.valid,
              approved: id.approved,
              rejected: id.rejected,
              editing: false,
              created_by_system: id.created_by_system,
            }))
          : [
              {
                source: 'spotify',
                itemId: `${artist.id}-spotify-0`,
                id: '',
                valid: false,
                approved: true,
                rejected: false,
                created_by_system: false,
              } satisfies BandId,
            ]),
        ...(artist.source_ids.musicbrainz.length > 0
          ? artist.source_ids.musicbrainz.map<BandId>((id, index) => ({
              source: 'musicbrainz',
              itemId: `${artist.id}-musicbrainz-${index}`,
              id: id.id,
              artistName: id.original_name,
              validated: true,
              valid: id.valid,
              approved: id.approved,
              rejected: id.rejected,
              editing: false,
              created_by_system: id.created_by_system,
            }))
          : [
              {
                source: 'musicbrainz',
                itemId: `${artist.id}-musicbrainz-0`,
                id: '',
                valid: false,
                approved: true,
                rejected: false,
                created_by_system: false,
              } satisfies BandId,
            ]),
      ],
      manualStageName: artist.manualStageName,
      hasAutoPopulated: artist.manualStageName ? true : false,
      errors: {},
    });
    setShowAddBandModal(true);
  };

  const handleOpenAddAliasModal = () => {
    setNewAliasForm(DEFAULT_ALIAS_FORM_STATE);
    setShowAddAliasModal(true);
  };

  const handleOpenAddBandModal = () => {
    setNewBandForm(DEFAULT_BAND_FORM_STATE);
    setShowAddBandModal(true);
  };

  const [expanded, setExpanded] = useState(false);

  const {
    startRun: runDiscography,
    restartRun: restartDiscography,
    status: runStatus,
    phase: runPhase,
    currentStep: runCurrentStep,
    totalSteps: runTotalSteps,
    currentStepLabel: runCurrentStepLabel,
    isStalled: runIsStalled,
    actionsDisabled: isFormDisabled,
    cancelRun: cancelDiscographyRun,
  } = usePrimaryDiscographyRun();

  const runStatusMap = {
    preparing: 'Preparing',
    'not-started': '',
    canceling: 'Canceling',
    cancelled: 'Cancelled',
    failed: 'Failed',
    finished: 'Finished',
    running: 'Running',
  };

  // Human label for the active phase shown next to the spinner.
  const activeRunLabel = (() => {
    switch (runPhase) {
      case 'REQUESTED':
        return 'Queued…';
      case 'LAUNCHING':
      case 'STARTING':
        return 'Starting (cold start can take a few minutes)…';
      case 'WAITING_FOR_SENSOR':
        return 'Waiting for next step…';
      case 'CANCELLING':
        return 'Cancelling…';
      case 'RUNNING':
        return runCurrentStepLabel && runCurrentStep && runTotalSteps
          ? `${runCurrentStepLabel} (${runCurrentStep}/${runTotalSteps})`
          : 'Running…';
      default:
        return runStatus === 'preparing'
          ? 'Preparing…'
          : runStatus === 'canceling'
          ? 'Cancelling…'
          : 'Running…';
    }
  })();

  return (
    <div
      className={`tw-h-full ${
        isSearchExpanded ? 'tw-w-[70%]' : 'tw-w-full'
      }  tw-shadow-lg tw-overflow-hidden tw-flex tw-flex-col tw-relative`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="tw-flex tw-flex-col tw-h-full tw-overflow-hidden">
        {/* Artist details card - more compact with row layout */}
        <div className="tw-rounded-lg tw-p-4 tw-mb-4 tw-flex-shrink-0">
          <div className="tw-flex tw-justify-between tw-items-center tw-mb-3">
            <h2 className="tw-text-white tw-text-lg tw-font-medium">
              Artist details
            </h2>
            {!isFormDisabled ? (
              <WButton
                variant="secondary"
                onClick={() => setShowEditArtistModal(true)}
                label={
                  <>
                    <Pencil size={16} className="tw-mr-1.5 tw-text-white" />
                    <span className="tw-text-white tw-text-sm">Edit</span>
                  </>
                }
              />
            ) : null}
          </div>
          <div className="tw-flex tw-flex-wrap tw-gap-4">
            <div className="tw-pr-4 tw-mb-3">
              <p className="tw-text-white tw-text-sm tw-mb-0.5">Name</p>
              <p className="tw-text-gray-400 tw-text-sm">
                {credentialsFormData.firstName} {credentialsFormData.lastName}
              </p>
            </div>
            <div className="tw-pr-4 tw-mb-3">
              <p className="tw-text-white tw-text-sm tw-mb-0.5">Email</p>
              <p className="tw-text-gray-400 tw-text-sm">
                {credentialsFormData.email}
              </p>
            </div>
            <div className="tw-pr-4 tw-mb-3">
              <p className="tw-text-white tw-text-sm tw-mb-0.5">
                PPL Contributor Name
              </p>
              <p className="tw-text-gray-400 tw-text-sm">
                {credentialsFormData.pplPerformerName ?? '-'}
              </p>
            </div>
            <div className="tw-pr-4 tw-mb-3">
              <p className="tw-text-white tw-text-sm tw-mb-0.5">
                PPL Performer ID
              </p>
              <p className="tw-text-gray-400 tw-text-sm">
                {credentialsFormData.pplPerformerId ?? '-'}
              </p>
            </div>
            <div className="tw-pr-4 tw-mb-3">
              <p className="tw-text-white tw-text-sm tw-mb-0.5">
                PPL Account Name
              </p>
              <p className="tw-text-gray-400 tw-text-sm">
                {credentialsFormData.pplAccountName ?? '-'}
              </p>
            </div>
          </div>
        </div>
        {/* Outcome banner. Non-blocking on purpose: it reports what happened to the last
            run without hiding the discography the admin came here to look at. */}
        {runNotice ? (
          <div
            className={`tw-rounded-lg tw-px-4 tw-py-3 tw-mb-4 tw-flex tw-items-start tw-justify-between tw-border ${
              runNotice.attention
                ? 'tw-border-amber-500/50 tw-bg-amber-500/10'
                : 'tw-border-gray-700 tw-bg-gray-800/60'
            }`}
          >
            {/* `detail` already embeds `errorDetail` for a failed run — rendering both
                printed the backend's message twice. */}
            <p
              className={`tw-text-sm tw-font-medium ${
                runNotice.attention ? 'tw-text-amber-300' : 'tw-text-gray-200'
              }`}
            >
              {runNotice.message}
              {/* The panel is where a collection is started. `toggleSearchPanel` would
                  close it if it were already open. */}
              {runNotice.offerSearch && !isSearchExpanded ? (
                <button
                  type="button"
                  onClick={toggleSearchPanel}
                  className="tw-ml-2 tw-underline hover:tw-text-white"
                >
                  Search IDs
                </button>
              ) : null}
            </p>
            <button
              type="button"
              aria-label="Dismiss"
              onClick={() => setDismissedNoticeKey(noticeKey)}
              className="tw-ml-4 tw-flex-shrink-0 tw-text-gray-400 hover:tw-text-white"
            >
              <X size={16} />
            </button>
          </div>
        ) : null}
        {/* Discography IDs card - make it fill available space */}
        <div className="tw-rounded-lg tw-p-4 tw-mb-4 tw-flex-grow tw-flex tw-flex-col tw-overflow-hidden">
          {isPopulatingIds ? (
            <div className="tw-flex tw-flex-col tw-h-full tw-overflow-hidden">
              <div className="tw-flex tw-items-center tw-justify-between tw-mb-3 tw-flex-shrink-0">
                <div className="tw-flex tw-items-center">
                  <h2 className="tw-text-white tw-text-lg tw-font-medium">
                    Discography IDs
                  </h2>
                  {/* <button className="tw-ml-2 tw-text-gray-400 tw-hover:text-white">
                    <HelpCircle size={16} />
                  </button> */}
                </div>
                <div className="tw-flex tw-space-x-2">
                  <WButton
                    onClick={toggleSearchPanel}
                    variant="secondary"
                    label={
                      <>
                        <Search size={14} className="tw-mr-1.5" />
                        Search IDs
                      </>
                    }
                  />
                </div>
              </div>
              <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-12 tw-flex-grow">
                <div className="tw-relative tw-mb-6">
                  <div className="tw-bg-westburyBlue tw-rounded-full tw-p-5 tw-animate-pulse">
                    {status === 'collecting' ? (
                      <Music size={32} className="tw-text-white" />
                    ) : (
                      <Cog
                        size={32}
                        className="tw-text-white tw-animate-spin"
                      />
                    )}
                  </div>
                  <div className="tw-absolute tw-inset-0 tw-rounded-full tw-border-4 tw-border-t-transparent tw-border-westburyBlue tw-animate-spin"></div>
                </div>
                {/* Name the stage and keep a clock on it. The reported complaint was not
                    that the job is slow but that the screen gave no evidence it was doing
                    anything, so the admin assumed it had not started. */}
                <p
                  className={`tw-text-center tw-mb-2 tw-text-lg ${
                    indicator.attention
                      ? 'tw-text-amber-400'
                      : 'tw-text-gray-100'
                  }`}
                >
                  {indicator.detail}
                </p>
                <p className="tw-text-gray-400 tw-text-center tw-text-sm tw-mb-8">
                  Populating discography IDs - this may take some time...
                </p>
                {clientId && canCancel ? (
                  <div className="tw-flex tw-w-full tw-items-center tw-justify-center">
                    <WButton
                      onClick={() => cancelAutoCollectIds(clientId)}
                      // Without this the button stayed live through the whole DELETE, so a
                      // second click fired a second cancel at the same run.
                      disabled={cancelAutoCollectIdsInProgress}
                      variant="secondary"
                      label={
                        <>
                          {cancelAutoCollectIdsInProgress ? (
                            <Loader2 className="tw-animate-spin" />
                          ) : (
                            'Cancel'
                          )}
                        </>
                      }
                    ></WButton>
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="tw-flex tw-flex-col tw-h-full tw-overflow-hidden">
              <div className="tw-flex tw-items-center tw-justify-between tw-mb-3 tw-flex-shrink-0">
                <div className="tw-flex tw-items-center">
                  <h2 className="tw-text-white tw-text-lg tw-font-medium">
                    Discography IDs
                  </h2>
                  {/* <button className="tw-ml-2 tw-text-gray-400 tw-hover:text-white">
                    <HelpCircle size={16} />
                  </button> */}
                </div>
                <div className="tw-flex tw-items-center tw-gap-2">
                  {!isFormDisabled ? (
                    <>
                      <WButton
                        onClick={handleOpenAddAliasModal}
                        label="+ Add Alias"
                        variant="secondary"
                      />
                      <WButton
                        onClick={handleOpenAddBandModal}
                        label="+ Add Group"
                        variant="secondary"
                      />
                    </>
                  ) : null}
                  <DiscographyIdFilterDropdown
                    value={tableFilter}
                    onChange={setTableFilter}
                  />
                  {/* Same 32px height, 2px border and inherited font as the secondary WButtons beside it. */}
                  <div className="tw-flex tw-items-center tw-leading-4">
                    <button
                      type="button"
                      onClick={() => setExpanded(true)}
                      className={`tw-box-border tw-h-[32px] tw-px-4 tw-[font:inherit] tw-rounded-l-3xl tw-border-solid tw-border-r-0 tw-border-2 ${
                        expanded
                          ? 'tw-border-surfaceLime tw-bg-surfaceLime tw-text-black'
                          : 'tw-border-white tw-bg-transparent hover:tw-border-surfaceLime hover:tw-bg-surfaceLime hover:tw-text-black'
                      }`}
                    >
                      Expanded
                    </button>
                    <button
                      type="button"
                      onClick={() => setExpanded(false)}
                      className={`tw-box-border tw-h-[32px] tw-px-4 tw-[font:inherit] tw-rounded-r-3xl tw-border-solid tw-border-l-0 tw-border-2 ${
                        !expanded
                          ? 'tw-bg-surfaceLime tw-border-surfaceLime tw-text-black'
                          : 'tw-bg-transparent tw-border-white hover:tw-border-surfaceLime hover:tw-bg-surfaceLime hover:tw-text-black'
                      }`}
                    >
                      Collapsed
                    </button>
                  </div>
                  <WButton
                    onClick={toggleSearchPanel}
                    variant="secondary"
                    label={
                      <>
                        <Search size={14} className="tw-mr-1.5" />
                        Search IDs
                      </>
                    }
                  />
                </div>
              </div>
              {/* Legend */}
              <div className="tw-pb-3 tw-pl-1 tw-flex tw-items-center tw-gap-3 tw-text-sm tw-text-gray-400">
                <div className="tw-flex tw-items-center tw-gap-1">
                  <span className="tw-w-4 tw-h-4 tw-bg-westburyBlue tw-rounded-full"></span>
                  <span>Validated</span>
                </div>
                <div className="tw-flex tw-items-center tw-gap-1">
                  <span className="tw-w-4 tw-h-4 tw-bg-westburyGreen tw-rounded-full"></span>
                  <span>Approved</span>
                </div>
                <div className="tw-flex tw-items-center tw-gap-1">
                  <span className="tw-w-4 tw-h-4 tw-bg-westburyAmber tw-rounded-full"></span>
                  <span>Review</span>
                </div>
                <div className="tw-flex tw-items-center tw-gap-1">
                  <span className="tw-w-4 tw-h-4 tw-bg-westburyRed tw-rounded-full"></span>
                  <span>Rejected</span>
                </div>
              </div>
              {idsPopulated ? (
                <div className="tw-w-full tw-overflow-hidden tw-flex-grow tw-flex tw-flex-col">
                  <div className="tw-rounded-lg tw-border tw-border-gray-700 tw-overflow-hidden tw-flex tw-flex-col tw-h-full">
                    {/* Table header - sticky */}
                    <div className="tw-sticky tw-top-0 tw-z-10">
                      <div className="tw-w-full tw-text-xs tw-text-left tw-bg-gray-800">
                        <div className="tw-text-xs tw-text-gray-400 tw-border-b tw-border-gray-700 tw-flex">
                          <div
                            className="tw-py-2 tw-px-3 tw-font-medium tw-w-1/5 tw-border-r tw-border-gray-700 tw-cursor-pointer tw-flex tw-items-center"
                            onClick={toggleSortDirection}
                          >
                            Alias/Group
                            {sortDirection === 'asc' ? (
                              <ChevronUp size={14} className="tw-ml-1" />
                            ) : (
                              <ChevronDown size={14} className="tw-ml-1" />
                            )}
                          </div>
                          <div className="tw-py-2 tw-px-3 tw-font-medium tw-w-1/5 tw-border-r tw-border-gray-700">
                            Discogs
                          </div>
                          <div className="tw-py-2 tw-px-3 tw-font-medium tw-w-1/5 tw-border-r tw-border-gray-700">
                            Deezer
                          </div>
                          <div className="tw-py-2 tw-px-3 tw-font-medium tw-w-1/5 tw-border-r tw-border-gray-700">
                            Spotify
                          </div>
                          <div className="tw-py-2 tw-px-3 tw-font-medium tw-w-1/5">
                            MusicBrainz
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Table body - scrollable */}
                    <div
                      className="tw-overflow-y-auto tw-overflow-x-visible tw-flex-grow tw-bg-gray-800"
                      style={{
                        scrollbarWidth: 'none',
                      }}
                      ref={tableBodyRef}
                    >
                      <div className="tw-w-full tw-text-xs tw-text-left">
                        {sortedArtistData.map((artist, index) => (
                          <div
                            key={artist.id}
                            className="tw-border-b tw-border-gray-700 tw-bg-gray-900 hover:tw-bg-gray-800 tw-flex"
                          >
                            <div className="tw-py-2 tw-px-3 tw-align-top tw-border-r tw-border-gray-700 tw-w-1/5 tw-flex tw-items-start">
                              <div className="tw-flex tw-w-full  tw-items-start tw-gap-2 tw-h-full">
                                <div className="tw-flex tw-w-full tw-justify-between tw-items-center tw-gap-2">
                                  <div className="tw-flex tw-justify-start">
                                    <a
                                      href="#"
                                      onClick={() =>
                                        handleOpenEditModal(
                                          artist.id,
                                          'discogs'
                                        )
                                      }
                                      className="tw-text-white hover:tw-underline tw-font-medium tw-inline-flex tw-items-center tw-gap-1"
                                    >
                                      {artist.stageName}
                                    </a>
                                  </div>
                                  <div className="tw-flex tw-items-center tw-gap-2">
                                    {artist.type === 'group' ? (
                                      <PermanentStatusCheck artist={artist} />
                                    ) : null}
                                    {!artist.created_by_system &&
                                    !isFormDisabled ? (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setConfirmDeleteModalDetails({
                                            stageName: artist.stageName,
                                            id: artist.id,
                                            type: artist.type,
                                          })
                                        }
                                        className="tw-group tw-p-0 tw-text-westburyRed tw-rounded-md hover:tw-text-white  tw-flex tw-items-center"
                                        title="Edit ID"
                                      >
                                        <Trash2
                                          size={18}
                                          className="tw-text-westburyRed group-hover:tw-text-white tw-cursor-pointer"
                                        />
                                      </button>
                                    ) : null}
                                  </div>
                                </div>
                              </div>
                            </div>
                            <ArtistDiscographySourceIdCell
                              expanded={expanded}
                              key={`${artist.id}-discogs-cell`}
                              artist={artist}
                              source="discogs"
                              handleAddIdFromCell={handleOpenEditModal}
                              openIdDetailsModal={handleOpenEditModal}
                            />
                            <ArtistDiscographySourceIdCell
                              expanded={expanded}
                              key={`${artist.id}-deezer-cell`}
                              artist={artist}
                              source="deezer"
                              handleAddIdFromCell={handleOpenEditModal}
                              openIdDetailsModal={handleOpenEditModal}
                            />
                            <ArtistDiscographySourceIdCell
                              expanded={expanded}
                              key={`${artist.id}-spotify-cell`}
                              artist={artist}
                              source="spotify"
                              handleAddIdFromCell={handleOpenEditModal}
                              openIdDetailsModal={handleOpenEditModal}
                            />
                            <ArtistDiscographySourceIdCell
                              expanded={expanded}
                              key={`${artist.id}-musicbrainz-cell`}
                              artist={artist}
                              source="musicbrainz"
                              handleAddIdFromCell={handleOpenEditModal}
                              openIdDetailsModal={handleOpenEditModal}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="tw-text-center tw-py-8 tw-flex-grow tw-flex tw-flex-col tw-justify-center">
                  <p className="tw-text-gray-400">No IDs populated yet</p>
                  <p className="tw-text-gray-500 tw-text-sm tw-mt-2">
                    Use the search function to populate IDs
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
        {/* Run Discography button */}
        {!isPopulatingIds && idsPopulated ? (
          <div className="tw-flex tw-justify-end tw-w-full tw-items-center tw-gap-4">
            {runStatus !== null &&
            (runStatus === 'cancelled' ||
              runStatus === 'failed' ||
              runStatus === 'finished') ? (
              <div className="tw-my-4">
                <p className="tw-flex tw-items-center tw-gap-2">
                  Last run status:{' '}
                  <span
                    className={[
                      'tw-rounded-full tw-w-4 tw-h-4',
                      runStatus === 'finished' ? 'tw-bg-westburyGreen' : '',
                      runStatus === 'cancelled' ? 'tw-bg-gray-500' : '',
                      runStatus === 'failed' ? 'tw-bg-westburyRed' : '',
                    ].join(' ')}
                  ></span>
                  {runStatusMap[runStatus] ?? 'Unknown'}
                </p>
              </div>
            ) : null}
            {runStatus !== null &&
            runStatus !== 'running' &&
            runStatus !== 'preparing' &&
            runStatus !== 'canceling' ? (
              <div className="tw-min-w-[200px] tw-pr-4">
                <Button
                  styleType="btn-primary"
                  onClick={runDiscography}
                  disabled={isFormDisabled}
                >
                  <div className="tw-w-full tw-flex tw-gap-2 tw-items-center">
                    {runStatus === 'not-started'
                      ? 'Run Discography'
                      : 'Re-Run Discography'}
                  </div>
                </Button>
              </div>
            ) : (
              <div className="tw-min-w-[260px] tw-flex tw-flex-col tw-gap-2 tw-items-end tw-justify-end tw-pr-4">
                <div className="tw-flex tw-gap-2 tw-items-center tw-justify-end">
                  <Button
                    styleType="btn-primary"
                    onClick={runDiscography}
                    disabled={isFormDisabled}
                  >
                    <div className="tw-w-full tw-flex tw-gap-2 tw-items-center">
                      {runStatus === 'preparing' ? (
                        <Cog size={18} className="tw-animate-spin" />
                      ) : (
                        <Loader2 size={18} className="tw-animate-spin" />
                      )}
                      {activeRunLabel}
                    </div>
                  </Button>
                  {runStatus === 'running' || runStatus === 'preparing' ? (
                    <Button
                      styleType="btn-primary"
                      onClick={cancelDiscographyRun}
                    >
                      <div className="tw-w-full tw-flex tw-gap-2 tw-items-center">
                        <Ban size={18} />
                        {'Cancel Discography Run'}
                      </div>
                    </Button>
                  ) : null}
                  {runIsStalled ? (
                    <Button
                      styleType="btn-primary"
                      onClick={restartDiscography}
                    >
                      <div className="tw-w-full tw-flex tw-gap-2 tw-items-center">
                        <RefreshCw size={18} />
                        {'Restart stuck run'}
                      </div>
                    </Button>
                  ) : null}
                </div>
                {runCurrentStep && runTotalSteps ? (
                  <div className="tw-h-1.5 tw-w-full tw-bg-gray-600 tw-rounded-full tw-overflow-hidden">
                    <div
                      className="tw-h-full tw-bg-westburyGreen tw-transition-all"
                      style={{
                        width: `${Math.round(
                          (runCurrentStep / runTotalSteps) * 100
                        )}%`,
                      }}
                    />
                  </div>
                ) : null}
                {runIsStalled ? (
                  <p className="tw-text-xs tw-text-westburyRed">
                    No progress detected — the run may be stuck. You can restart
                    it.
                  </p>
                ) : null}
              </div>
            )}
          </div>
        ) : null}
      </div>
      {/* Artist Details Edit Modal */}
      {showEditArtistModal && (
        <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-70 tw-flex tw-items-center tw-justify-center tw-z-50">
          <div className="tw-bg-gray-800 tw-rounded-lg tw-p-6 tw-max-w-md tw-w-full tw-mx-4">
            <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
              <h3 className="tw-text-white tw-text-lg tw-font-medium">
                Edit Artist Details
              </h3>
              <button
                onClick={() => setShowEditArtistModal(false)}
                className="tw-text-gray-400 hover:tw-text-white"
              >
                <X size={20} />
              </button>
            </div>
            <div className="tw-space-y-4">
              {/* Artist First Name */}
              <div>
                <label
                  htmlFor="firstName"
                  className="tw-text-white tw-mb-1 tw-flex tw-items-center"
                >
                  Name
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={credentialsFormData.firstName}
                  onChange={(e) =>
                    setCredentialsFormData((state) => ({
                      ...state,
                      firstName: e.target.value,
                    }))
                  }
                  className="tw-w-full tw-bg-gray-700 tw-border-none tw-rounded-md tw-px-3 tw-py-2 tw-text-white tw-outline-none"
                  required
                />
              </div>
              {/* Artist Last Name */}
              <div>
                <label
                  htmlFor="lastName"
                  className=" tw-text-white tw-mb-1 tw-flex tw-items-center"
                >
                  Name
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={credentialsFormData.lastName}
                  onChange={(e) =>
                    setCredentialsFormData((state) => ({
                      ...state,
                      lastName: e.target.value,
                    }))
                  }
                  className="tw-w-full tw-bg-gray-700 tw-border-none tw-rounded-md tw-px-3 tw-py-2 tw-text-white tw-outline-none"
                  required
                />
              </div>
              {/* Email */}
              <div>
                <label
                  htmlFor="email"
                  className=" tw-text-white tw-mb-1 tw-flex tw-items-center"
                >
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={credentialsFormData.email}
                  onChange={(e) =>
                    setCredentialsFormData((state) => ({
                      ...state,
                      email: e.target.value,
                    }))
                  }
                  className="tw-w-full tw-bg-gray-700 tw-border-none tw-rounded-md tw-px-3 tw-py-2 tw-text-white tw-outline-none"
                  required
                />
              </div>
              {/* PPL Contributor Name */}
              <div>
                <label
                  htmlFor="pplPerformerName"
                  className=" tw-text-white tw-mb-1 tw-flex tw-items-center"
                >
                  PPL Contributor Name
                </label>
                <input
                  type="text"
                  name="pplPerformerName"
                  value={credentialsFormData.pplPerformerName}
                  onChange={(e) =>
                    setCredentialsFormData((state) => ({
                      ...state,
                      pplPerformerName: e.target.value,
                    }))
                  }
                  className="tw-w-full tw-bg-gray-700 tw-border-none tw-rounded-md tw-px-3 tw-py-2 tw-text-white tw-outline-none"
                  required
                />
              </div>
              {/* PPL Performer ID */}
              <div>
                <label
                  htmlFor="pplPerformerId"
                  className=" tw-text-white tw-mb-1 tw-flex tw-items-center"
                >
                  PPL Perfomer ID
                </label>
                <input
                  type="text"
                  name="pplPerformerId"
                  value={credentialsFormData.pplPerformerId}
                  onChange={(e) =>
                    setCredentialsFormData((state) => ({
                      ...state,
                      pplPerformerId: e.target.value,
                    }))
                  }
                  className="tw-w-full tw-bg-gray-700 tw-border-none tw-rounded-md tw-px-3 tw-py-2 tw-text-white tw-outline-none"
                  required
                />
              </div>
              {/* PPL Account Name */}
              <div>
                <label
                  htmlFor="pplAccountName"
                  className=" tw-text-white tw-mb-1 tw-flex tw-items-center"
                >
                  PPL Account Name
                </label>
                <input
                  type="text"
                  name="pplAccountName"
                  value={credentialsFormData.pplAccountName}
                  onChange={(e) =>
                    setCredentialsFormData((state) => ({
                      ...state,
                      pplAccountName: e.target.value,
                    }))
                  }
                  className="tw-w-full tw-bg-gray-700 tw-border-none tw-rounded-md tw-px-3 tw-py-2 tw-text-white tw-outline-none"
                  required
                />
              </div>
            </div>
            <div className="tw-flex tw-justify-end tw-space-x-3 tw-mt-6">
              <button
                type="button"
                onClick={() => {
                  setShowEditArtistModal(false);
                  // Reset values
                  setCredentialsFormData(cleanCredentialsFormData);
                }}
                className="tw-px-4 tw-py-2 tw-rounded-md tw-text-white tw-bg-gray-700 hover:tw-bg-gray-600"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowEditArtistModal(false);
                  onUserSettingsUpdated();
                  // Update values to the latest
                  setCleanCredentialsFormData(credentialsFormData);
                }}
                className="tw-px-4 tw-py-2 tw-rounded-md tw-text-white tw-bg-westburyBlue hover:tw-bg-westburyBlue/80"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ID Details Modal */}
      {showIdDetailsModal && selectedIdDetails && (
        <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-70 tw-flex tw-items-center tw-justify-center tw-z-50">
          <div className="tw-bg-gray-800 tw-rounded-lg tw-p-6 tw-max-w-md tw-w-full tw-mx-4">
            <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
              <h3 className="tw-text-white tw-text-lg tw-font-medium">
                {selectedIdDetails.platform.charAt(0).toUpperCase() +
                  selectedIdDetails.platform.slice(1)}{' '}
                IDs for {selectedIdDetails.artistName}
              </h3>
              <button
                onClick={() => setShowIdDetailsModal(false)}
                className="tw-text-gray-400 hover:tw-text-white"
              >
                <X size={20} />
              </button>
            </div>
            <div className="tw-space-y-3 tw-max-h-96 tw-overflow-y-auto tw-mb-6">
              {selectedIdDetails.source_ids.length > 0 ? (
                selectedIdDetails.source_ids.map((idObj, index) => (
                  <div
                    key={index}
                    className="tw-bg-gray-700 tw-rounded-md tw-p-3"
                  >
                    <div className="tw-text-gray-300 tw-text-xs tw-mb-1">
                      {idObj.original_name}
                    </div>
                    <div className="tw-flex tw-items-center tw-justify-between">
                      <div className="tw-flex tw-items-center">
                        <a
                          href={generateArtistUrlBySource(
                            idObj.id,
                            selectedIdDetails.platform
                          )}
                          className="tw-text-westburyBlue tw-text-sm tw-flex tw-items-center tw-justify-center"
                        >
                          {idObj.id}
                        </a>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="tw-text-gray-400 tw-text-center tw-py-4">
                  No IDs available
                </p>
              )}
            </div>
            <div className="tw-flex tw-justify-end tw-space-x-3">
              <button
                onClick={() => setShowIdDetailsModal(false)}
                className="tw-px-4 tw-py-2 tw-rounded-md tw-text-white tw-bg-gray-700 hover:tw-bg-gray-600"
              >
                Close
              </button>
              {/* <button
                onClick={() => setShowIdDetailsModal(false)}
                className="tw-px-4 tw-py-2 tw-rounded-md tw-text-white tw-bg-westburyBlue hover:tw-bg-westburyBlue/80 tw-flex tw-items-center"
              >
                <Plus size={16} className="mr-1.5" />
                Add ID
              </button> */}
            </div>
          </div>
        </div>
      )}
      <AliasBandManagement
        showAliasModal={showAddAliasModal}
        showBandModal={showAddBandModal}
        onClose={() => {
          setShowAddAliasModal(false);
          setShowAddBandModal(false);
        }}
        onCreateNewAlias={() => {
          setShowAddBandModal(false);
          handleOpenAddAliasModal();
        }}
        activeAliasTab={activeAliasTab}
        activeBandTab={activeBandTab}
        onAliasTabChange={setActiveAliasTab}
        onBandTabChange={setActiveBandTab}
        aliasForm={newAliasForm}
        bandForm={newBandForm}
        onAliasChange={setNewAliasForm}
        onBandChange={setNewBandForm}
      />
      {confirmDeleteModalDetails ? (
        <WModal
          onClose={() => setConfirmDeleteModalDetails(null)}
          isOpen={Boolean(confirmDeleteModalDetails)}
          title={`Remove "${confirmDeleteModalDetails.stageName}" ${
            confirmDeleteModalDetails.type === 'persona' ? 'alias' : 'band'
          }?`}
        >
          <div className="tw-flex tw-flex-col">
            <div>
              {`Are you sure you want to remove "${
                confirmDeleteModalDetails.stageName
              }" ${
                confirmDeleteModalDetails.type === 'persona' ? 'alias' : 'band'
              }?`}
            </div>
            <div className="tw-mt-4 tw-w-full tw-flex tw-justify-between">
              <WButton
                label="Cancel"
                variant="secondary"
                onClick={() => {
                  setConfirmDeleteModalDetails(null);
                }}
              />
              <WButton
                label="Continue"
                variant="primary"
                onClick={() => {
                  handleDeleteArtistDiscographyID(confirmDeleteModalDetails.id);
                  setConfirmDeleteModalDetails(null);
                }}
              />
            </div>
          </div>
        </WModal>
      ) : null}
    </div>
  );
}
